import React, { useState } from 'react'

import {
    FormControlLabel,
    IconButton,
    Popover,
    Switch,
    Tooltip,
    useTheme,
    Typography,
} from '@material-ui/core'
import LocalAtmIcon from '@material-ui/icons/LocalAtm'
import AccountTreeIcon from '@material-ui/icons/AccountTree'
import PeopleIcon from '@material-ui/icons/People'

import { Container, EditSpan, MultiSelector, Selector } from '../../components'
import { useAppDispatch } from '../../hooks'
import { usePopover } from '../../hooks/usePopover'
import { InventoryConfig, LeaseStatus, StatusGroup } from '../../models'
import { updateInventoryConfigReq } from '../../store'

import { useInfrastructureConfigStyles } from './useInfrastructureConfigStyles'
import { PriorityHigh } from '@material-ui/icons'
import { typographyStyle } from '../../theme/typographyStyle'

const LEASE_STATUS_OPTIONS = [
    { id: LeaseStatus.APPLICANT, name: 'Applicant' },
    { id: LeaseStatus.CANCELLED, name: 'Cancelled' },
    { id: LeaseStatus.FUTURE, name: 'Future' },
    { id: LeaseStatus.CURRENT, name: 'Current' },
    { id: LeaseStatus.NOTICE, name: 'Notice' },
    { id: LeaseStatus.PAST, name: 'Past' },
]

interface Props {
    inventoryConfig: InventoryConfig
    statusGroupList?: StatusGroup[]
    selected: boolean
    onClickTree: (id: number) => void
    onClickCost: (invConfig: InventoryConfig) => void
    onClickInfrastructure: (id: Number) => void
    onClickIssue: (id: number) => void
}

export const _InventoryConfigRow = (props: Props) => {
    const { inventoryConfig, selected } = props

    const styles = useInfrastructureConfigStyles()
    const theme = useTheme()

    const [shortAnswer, setShortAnswer] = useState(inventoryConfig.ins_has_note)
    const [tenantVisibleStatuses, setTenantVisibleStatuses] = useState<
        number[]
    >(inventoryConfig.tenant_visible_statuses ?? [])

    const dispatch = useAppDispatch()
    const tenantVisPopover = usePopover()
    const [statusGroupId, setStatusGroupId] = useState(
        inventoryConfig.status_group?.id ?? -1,
    )

    let userFriendlySKU = 'Add SKU'
    if (inventoryConfig.sku !== '') {
        userFriendlySKU = `SKU: ${inventoryConfig.sku}`
    }

    let userFriendlyDescription = 'Add Description'
    if (inventoryConfig.description !== '') {
        userFriendlyDescription = inventoryConfig.description
    }

    const updateInventoryConfig = (key: string, value: string) => {
        const invConfigFormData = new FormData()
        invConfigFormData.append(key, value)
        dispatch(
            updateInventoryConfigReq({
                id: inventoryConfig.id,
                req: invConfigFormData,
            }),
        )
    }

    return (
        <Container
            style={{
                ...styles.rowStyle,
                ...styles.panelBorder,

                backgroundColor: selected
                    ? theme.palette.primary.light
                    : theme.palette.grey[200],
            }}
        >
            <Container
                style={{
                    flexDirection: 'column',
                    marginRight: theme.spacing(1),
                    minWidth: '200px',
                }}
            >
                <EditSpan
                    style={{
                        ...typographyStyle(theme.typography.body1),
                        fontWeight: 700,
                    }}
                    maxLength={30}
                    editIconTooltip="Edit Name"
                    onSave={(name) => {
                        updateInventoryConfig('name', name)
                    }}
                >
                    {inventoryConfig.name}
                </EditSpan>

                <FormControlLabel
                    control={
                        <Switch
                            checked={shortAnswer}
                            color="primary"
                            onChange={() => {
                                setShortAnswer(!shortAnswer)
                                updateInventoryConfig(
                                    'ins_has_note',
                                    String(!shortAnswer),
                                )
                            }}
                        />
                    }
                    labelPlacement="end"
                    label="Short Answer Question"
                />
                {!shortAnswer && (
                    <EditSpan
                        style={{
                            ...typographyStyle(theme.typography.body1),
                            marginTop: theme.spacing(1),
                        }}
                        editIconTooltip="Choose Status Group"
                        onSave={() => {
                            updateInventoryConfig(
                                'status_group',
                                statusGroupId.toString(),
                            )
                        }}
                        customPopoverContent={
                            <Container style={{ flexDirection: 'column' }}>
                                <Selector
                                    label="Status Group"
                                    currentValue={statusGroupId}
                                    data={props.statusGroupList ?? []}
                                    customStyle={{
                                        formControl: { margin: 0, width: 200 },
                                    }}
                                    onChange={(
                                        event: React.ChangeEvent<{
                                            value: unknown
                                        }>,
                                    ) => {
                                        const newDefaultGroup = props.statusGroupList?.find(
                                            (s) =>
                                                s.id ===
                                                (event.target.value as number),
                                        )
                                        setStatusGroupId(
                                            newDefaultGroup?.id ?? -1,
                                        )
                                    }}
                                    getDisplayString={(s: StatusGroup) =>
                                        `${s.name}`
                                    }
                                />
                            </Container>
                        }
                    >
                        {inventoryConfig.status_group?.name ??
                            'Add Status Group'}
                    </EditSpan>
                )}

                <EditSpan
                    style={{
                        ...typographyStyle(theme.typography.body1),
                        marginTop: theme.spacing(1),
                    }}
                    editIconTooltip="Edit SKU"
                    maxLength={50}
                    onSave={(sku) => {
                        updateInventoryConfig('sku', sku)
                    }}
                >
                    {userFriendlySKU}
                </EditSpan>
            </Container>

            <Container style={{ flexDirection: 'column', minWidth: '200px' }}>
                <EditSpan
                    style={{ ...typographyStyle(theme.typography.body1) }}
                    onSave={(description) => {
                        updateInventoryConfig('description', description)
                    }}
                >
                    {userFriendlyDescription}
                </EditSpan>
            </Container>

            <Container
                style={{
                    flexDirection: 'column',
                    margin: theme.spacing(0, 2),
                    flexWrap: 'wrap',
                }}
            >
                {inventoryConfig.area_configs &&
                    inventoryConfig.area_configs.length > 0 && (
                        <Typography
                            variant="body2"
                            style={{
                                color: theme.palette.text.secondary,
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: theme.spacing(0.5),
                                flexWrap: 'wrap',
                                lineHeight: 1.2,
                            }}
                        >
                            <span
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: theme.spacing(0.5),
                                }}
                            >
                                {inventoryConfig.area_configs
                                    .map((ac: { id: number; name: string }) => (
                                        <span key={ac.id}>{ac.name}</span>
                                    ))
                                    .reduce(
                                        (prev, curr) =>
                                            [prev, ', ', curr] as any,
                                    )}
                            </span>
                        </Typography>
                    )}
            </Container>

            <div style={{ flex: 1 }} />

            <Container style={{ flexDirection: 'column' }}>
                <Tooltip title="Add to Areas">
                    <IconButton
                        onClick={() => props.onClickTree(inventoryConfig.id)}
                    >
                        <AccountTreeIcon />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Configure Costs">
                    <IconButton
                        onClick={() => props.onClickCost(inventoryConfig)}
                    >
                        <LocalAtmIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Configure Issues">
                    <IconButton
                        onClick={() => props.onClickIssue(inventoryConfig.id)}
                    >
                        <PriorityHigh />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Tenant Visibility">
                    <IconButton onClick={tenantVisPopover.handleOpen}>
                        <PeopleIcon
                            htmlColor={
                                tenantVisibleStatuses.length > 0
                                    ? theme.palette.primary.main
                                    : undefined
                            }
                        />
                    </IconButton>
                </Tooltip>
                <Popover
                    open={tenantVisPopover.isOpen}
                    anchorEl={tenantVisPopover.anchorEl}
                    onClose={tenantVisPopover.handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <Container
                        style={{
                            flexDirection: 'column',
                            padding: theme.spacing(2),
                            minWidth: 260,
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            style={{ marginBottom: theme.spacing(1) }}
                        >
                            Tenant Visibility by Lease Status
                        </Typography>
                        <Typography
                            variant="caption"
                            style={{
                                color: theme.palette.text.secondary,
                                marginBottom: theme.spacing(1.5),
                            }}
                        >
                            {tenantVisibleStatuses.length === 0
                                ? 'Visible to all tenants (default)'
                                : 'Only tenants with selected statuses can view'}
                        </Typography>
                        <MultiSelector
                            label="Lease Statuses"
                            keyIdentifier={`TENANT_VIS_${inventoryConfig.id}`}
                            options={LEASE_STATUS_OPTIONS}
                            selectedOptions={tenantVisibleStatuses}
                            onChange={(newStatuses) => {
                                setTenantVisibleStatuses(newStatuses)
                                dispatch(
                                    updateInventoryConfigReq({
                                        id: inventoryConfig.id,
                                        req: {
                                            tenant_visible_statuses: newStatuses,
                                        },
                                    }),
                                )
                            }}
                            variant="chips"
                            style={{
                                formControl: { margin: 0, width: '100%' },
                            }}
                        />
                    </Container>
                </Popover>
            </Container>
        </Container>
    )
}

export const InventoryConfigRow = React.memo(_InventoryConfigRow)
